/**
 * Which desks are out of service *now*, and therefore hidden from the plan by default.
 *
 * Two states get confused easily and must not be: a desk **retiring** on a future date is still
 * in service today — people can book it, and hiding it would leave an administrator wondering
 * where it went — while a desk whose date has arrived is gone from every booking surface and has
 * no business being drawn as if it were available.
 *
 * So the rule is `retiresOn <= today`, evaluated against **today at the office**, not the
 * browser's today. Everything else in this feature resolves that boundary server-side for the
 * same reason: a device in another timezone would hide a desk a day early or a day late.
 *
 * Nothing here touches the plan itself. Hiding is a view setting; the document that gets saved
 * keeps every feature it loaded, which is what stops a hidden desk being silently deleted by the
 * next save (DESK_RETIREMENT_PLAN.md 2.1).
 */

/**
 * Today at the office, from the server's own retirement boundary.
 *
 * `earliestRetirementDate` is today + `planPeriodDays` in the office's booking timezone, so
 * subtracting the period gives back the office's today without consulting the device clock.
 * Returns undefined when the server has not said — in which case nothing is hidden, because
 * guessing would hide the wrong desks.
 */
export const officeTodayFrom = (
  earliestRetirementDate: string | undefined,
  planPeriodDays: number,
): string | undefined => {
  if (!earliestRetirementDate) return undefined;

  // A plan period of zero is not a plausible office setting - the server clamps it to at least one
  // - so it means the field was missing from the response. Subtracting it would make "today" the
  // far end of the booking window instead, and every desk retiring anywhere inside that window
  // would be hidden while it is still perfectly bookable. Hiding nothing is the safe answer.
  if (!Number.isFinite(planPeriodDays) || planPeriodDays < 1) return undefined;

  const parts = earliestRetirementDate.split('-').map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return undefined;

  const [year, month, day] = parts;
  // UTC arithmetic on a bare calendar date: Date.UTC avoids the local-midnight shift that would
  // otherwise move the result by a day for anybody west of Greenwich.
  const date = new Date(Date.UTC(year, month - 1, day - planPeriodDays));

  return date.toISOString().slice(0, 10);
};

/**
 * The feature ids of desks whose retirement date has arrived.
 *
 * `retirementDates` is keyed by workplace id, which is also the desk's `featureId` — that
 * equality is what ties a dot on the plan to a bookable row.
 */
export const retiredDeskIdsAsOf = (
  retirementDates: ReadonlyMap<string, string>,
  officeToday: string | undefined,
): ReadonlySet<string> => {
  if (!officeToday) return new Set();

  const retired = new Set<string>();

  retirementDates.forEach((retiresOn, featureId) => {
    // String comparison is exact for yyyy-MM-dd and avoids constructing a Date per desk on a
    // plan that can hold hundreds.
    if (retiresOn <= officeToday) retired.add(featureId);
  });

  return retired;
};

export default retiredDeskIdsAsOf;
